import { action, observable, when } from 'mobx';

import type { TrackingInteractiveQuestionType } from '@genially/contracts';
import type {
  GameState,
  GameStateCompressed,
  GameStatePatchWithVersion,
  HostBenchmarkMessage,
  HostPeerMetadata,
  PlayerData,
  PlayerPeerMetadata,
  PlayerSendInteractiveQuestionAnswerMessage,
  PlayerSendScoreableInteractiveQuestionAnswerMessage,
  PlayerUserAnswer,
  PlayP2pHostMessage,
  PlayP2pPlayerMessage,
  PlayWebsocketServerToClientMessage,
} from '@genially/p2p-lib';
import {
  BaseErrorCodes,
  isGameState,
  isPlayerUserAnswerScoreable,
  PlayP2pHostMessageType,
  PlayP2pPlayerMessageType,
  PlayUserType,
} from '@genially/p2p-lib';
import {
  addJitter,
  addJitterToTime,
  createExponentialRetryStrategy,
  MeasurePerformanceService,
  sleep,
  withRetries,
} from '@genially/utils';

import { createLogger, createOnlyRemoteLogger, LoggerLevel } from '../../../util/logger';
import { PeerSyncMessageTimeoutError } from '../errors/PeerSyncMessageTimeoutError';
import { UnconnectedPeerError } from '../errors/UnconnectedPeerError';
import type { PlayP2pClientEmittedEvents } from '../PlayP2pClient';
import { PlayP2pClient } from '../PlayP2pClient';
import { calculateStringSizeInKb } from './utils/calculateStringSizeInKb';
import { hasGameFinishedPatch } from './utils/hasGameFinishedPatch';

export enum PlayerPlayP2pClientEventType {
  ConnectedToHost = 'connected-to-host',
  DisconnectedFromHost = 'disconnected-from-host',
  PlayerJoined = 'player-joined',
  PlayerDisconnected = 'player-disconnected',
  PlayersSync = 'players-sync',
  ChangeSlideCommand = 'change-slide',
  GameChanged = 'game-changed',
  Kicked = 'kicked',
  GameFinished = 'game-finished',
  SessionBlocked = 'session-blocked',
  PlayersLimitReached = 'players-limit-reached',
  NoGameFound = 'no-game-found',
  AllowPlayerToJoin = 'allow-player-to-join',
  InvalidUsername = 'invalid-username',

  InteractiveQuestionAnswered = 'interactive-question-answered',
  InteractiveQuestionResolved = 'interactive-question-resolved',
  ToggleOpenAnswerVisibility = 'toggle-open-answer-visibility',

  MarkAsTrackedInLogRocket = 'mark-as-tracked-in-logrocket',
}

export type PlayerPlayP2pClientEmittedEvents = PlayP2pClientEmittedEvents<{
  [PlayerPlayP2pClientEventType.ConnectedToHost]: [];
  [PlayerPlayP2pClientEventType.DisconnectedFromHost]: [];
  [PlayerPlayP2pClientEventType.PlayerJoined]: [
    { joinedPlayer: PlayerData; allPlayers: PlayerData[] },
  ];
  [PlayerPlayP2pClientEventType.PlayerDisconnected]: [
    { disconnectedPlayerId: string; allPlayers: PlayerData[] },
  ];
  [PlayerPlayP2pClientEventType.ChangeSlideCommand]: [string];
  [PlayerPlayP2pClientEventType.GameChanged]: [GameState];
  [PlayerPlayP2pClientEventType.Kicked]: [];
  [PlayerPlayP2pClientEventType.PlayersSync]: [PlayerData[]];
  [PlayerPlayP2pClientEventType.GameFinished]: [];
  [PlayerPlayP2pClientEventType.InvalidUsername]: [];
  [PlayerPlayP2pClientEventType.SessionBlocked]: [];
  [PlayerPlayP2pClientEventType.PlayersLimitReached]: [];
  [PlayerPlayP2pClientEventType.InteractiveQuestionAnswered]: [
    {
      interactiveQuestionId: string;
      type: TrackingInteractiveQuestionType;
      isEmptyAnswer?: boolean;
    },
  ];
  [PlayerPlayP2pClientEventType.InteractiveQuestionResolved]: [
    { interactiveQuestionId: string; isLastScored?: boolean },
  ];
  [PlayerPlayP2pClientEventType.NoGameFound]: [];
  [PlayerPlayP2pClientEventType.AllowPlayerToJoin]: [];
  [PlayerPlayP2pClientEventType.ToggleOpenAnswerVisibility]: [
    { interactiveQuestionId: string; userAlias: string; isHidden: boolean },
  ];
  [PlayerPlayP2pClientEventType.MarkAsTrackedInLogRocket]: [];
}>;

export type PlayerPlayP2pClientConstructorParams = {
  geniallyId: string;
  sessionId: string;
  username: string;
  initialSlide: string;
};

type PeerMetadata = HostPeerMetadata | PlayerPeerMetadata;

const MaxNoGameFoundRetries = 10;

const LoggerCreationMetadata = {
  minLoggerLevel: LoggerLevel.Debug,
  prefix: 'PlayerPlayP2pClient',
  app: '@genially/play',
};

export class PlayerPlayP2pClient extends PlayP2pClient<
  PlayerPlayP2pClientEmittedEvents,
  PeerMetadata
> {
  protected readonly _logger = createLogger(LoggerCreationMetadata);

  protected readonly measurePerformanceService = new MeasurePerformanceService(
    'PlayerPlayP2pClient',
  );

  public readonly username: string;

  @observable
  private _connectedToHost = false;

  private noGameFoundRetries = 0;

  @observable
  private _players: PlayerData[] = [];

  constructor({
    geniallyId,
    initialSlide,
    sessionId,
    username,
  }: PlayerPlayP2pClientConstructorParams) {
    const deviceId = PlayP2pClient.generateDeviceId();

    super({
      type: PlayUserType.Player,
      deviceId,
      sessionId,
      username,
      game: {
        isTrackedInLogRocket: false,
        currentSlide: initialSlide,
        isStarted: false,
        isFinished: false,
        startedAt: undefined,
        finishedAt: undefined,
        isSessionBlocked: false,
        version: 0,
        interactiveQuestionAnswers: {},
      },
      geniallyId,
      featureFlags: [],
    });

    this.disposers.push(
      when(
        () => this.game.isFinished,
        () => {
          this.finishGame();
        },
      ),
    );

    this.disposers.push(
      when(
        () => this.game.isTrackedInLogRocket,
        () => {
          this.emitter.emit(PlayerPlayP2pClientEventType.MarkAsTrackedInLogRocket);
        },
      ),
    );

    this.username = username;

    const remoteLogger = createOnlyRemoteLogger(LoggerCreationMetadata);

    this.measurePerformanceService.init(
      (message, metadata) =>
        remoteLogger.debug(message, {
          ...metadata,
          username: this.username,
          sessionId: this.sessionId,
          geniallyId: this.geniallyId,
          deviceId: this.deviceId,
          type: this.type,
          pingTime: this.pingTime,
          peerId: this._peerId ?? 'undefined',
        }),
      {
        threshold: 5,
      },
    );

    this.measurePerformanceService.measureThis(this, {
      ignoredMethods: ['peerId', 'debugInfo', 'connection', 'myPlayerData'],
    });
  }

  buildTransportParams(): Record<string, string> {
    return {
      ...super.buildTransportParams(),
      username: this.username,
    };
  }

  get isConnectedToHost() {
    return this._connectedToHost;
  }

  async start(): Promise<void> {
    if (!PlayerPlayP2pClient.isValidUsername(this.username)) {
      this.logger.error('Invalid username');

      this.emitter.emit(PlayerPlayP2pClientEventType.InvalidUsername);

      return;
    }

    await super.start();
  }

  onPeerIdReceived() {
    // Nothing to do here
  }

  get logger() {
    this._logger.setMetadata({
      geniallyId: this.geniallyId,
      username: this.username,
      sessionId: this.sessionId,
      gameSize: calculateStringSizeInKb(JSON.stringify(this._game.asJS)),
      gameSizeCompressed: calculateStringSizeInKb(
        JSON.stringify(this.gameCompressor.compress(this._game.asJS)),
      ),
      deviceId: this.deviceId,
      averageSyncResponseTime: this.getAverageSyncResponseTime(),
      pingTime: this.pingTime,
    });

    return this._logger;
  }

  get players() {
    return this._players;
  }

  get myPlayerData(): PlayerData {
    return {
      id: this.peerId,
      username: this.username,
    };
  }

  protected onServerError(code: BaseErrorCodes): {
    handled: boolean;
  } {
    const { handled } = super.onServerError(code);

    if (handled) return { handled };

    switch (code) {
      case BaseErrorCodes.NoRoomFound:
        this.handleNoGameFoundError();

        return { handled: true };
      default:
        return { handled: false };
    }
  }

  protected setConnection(peerId: string, metadata: PlayerPeerMetadata): boolean {
    return super.setConnection(peerId, metadata);
  }

  private async handleNoGameFoundError() {
    this.noGameFoundRetries += 1;

    if (this.noGameFoundRetries > MaxNoGameFoundRetries) {
      this.logger.warn(
        `No game found after ${MaxNoGameFoundRetries} retries. Disposing client`,
      );

      this.emitter.emit(PlayerPlayP2pClientEventType.NoGameFound);
      this.dispose();

      return;
    }

    this.disconnectFromServer();

    await sleep(addJitterToTime(5000));

    await this.connectToServer();
  }

  protected onWebsocketMessage(_: PlayWebsocketServerToClientMessage) {
    // Nothing to do here
  }

  @action
  protected onPeerConnected(peerId: string, metadata: PeerMetadata) {
    if (metadata.type === PlayUserType.Player) {
      this.addNewPlayer({
        id: peerId,
        username: metadata.username,
      });
    }

    if (metadata.type === PlayUserType.Host) {
      this._connectedToHost = true;

      this.emitter.emit(PlayerPlayP2pClientEventType.ConnectedToHost);
    }
  }

  @action
  protected onPeerDisconnected(peerId: string, metadata: PeerMetadata) {
    if (metadata.type === PlayUserType.Player) {
      this.removePlayer(peerId);
    }

    if (metadata.type === PlayUserType.Host) {
      this._connectedToHost = false;
    }
  }

  private sendToHost(message: Omit<PlayP2pPlayerMessage, 'gameVersion'>) {
    // Players only send messages to the host so we can ignore the peerId
    this.sendToPeer('', this.appendGameVersionToMessage(message));
  }

  private sendToHostSync(message: Omit<PlayP2pPlayerMessage, 'gameVersion'>) {
    // Players only send messages to the host so we can ignore the peerId
    return this.sendToPeerSync('', this.appendGameVersionToMessage(message));
  }

  private sendGameStateToHost() {
    this.sendToHost({
      type: PlayP2pPlayerMessageType.SendGameState,
      payload: {
        game: this.gameCompressor.compress(this._game.asJS),
      },
    });
  }

  protected async onPeerMessage(_: string, message: PlayP2pHostMessage) {
    if (this.disposed) return;

    switch (message.type) {
      case PlayP2pHostMessageType.KickPlayer:
        this.kickMySelf();
        break;
      case PlayP2pHostMessageType.SendGameState:
        this.handleGameStateReceivedFromHost(message.payload.game);
        break;
      case PlayP2pHostMessageType.SendGameStatePatches:
        this.handleGameStatePatchesReceivedFromHost(message.payload);
        break;
      case PlayP2pHostMessageType.RequestGameState:
        this.sendGameStateToHost();
        break;
      case PlayP2pHostMessageType.AllowPlayerToJoin:
        this.setFeatureFlags(message.payload.featureFlags);
        this.handleAllowPlayerToJoin();
        break;
      case PlayP2pHostMessageType.SessionBlocked:
        this.handleSessionBlocked();
        break;
      case PlayP2pHostMessageType.PlayersLimitReached:
        this.handlePlayersLimitReached();
        break;
      case PlayP2pHostMessageType.Benchmark:
        this.handleBenchmark(message.payload);
        break;
      case PlayP2pHostMessageType.InteractiveQuestionResolved:
        this.handleInteractiveQuestionResolved(
          message.payload.interactiveQuestionId,
          message.payload.isLastScored,
        );
        break;
      case PlayP2pHostMessageType.ToggleOpenAnswerVisibility:
        this.handleToggleOpenAnswerVisibility(
          message.payload.interactiveQuestionId,
          message.payload.userAlias,
          message.payload.isHidden,
        );
        break;
      default:
        this.logger.error(`Unhandled message type: ${message.type}`, { message });
        break;
    }
  }

  private handleToggleOpenAnswerVisibility(
    interactiveQuestionId: string,
    userAlias: string,
    isHidden: boolean,
  ) {
    this.emitter.emit(PlayerPlayP2pClientEventType.ToggleOpenAnswerVisibility, {
      interactiveQuestionId,
      userAlias,
      isHidden,
    });
  }

  private handleInteractiveQuestionResolved(
    interactiveQuestionId: string,
    isLastScored?: boolean,
  ) {
    this.logger.debug('Host resolved interactive question', {
      interactiveQuestionId,
    });

    this.emitter.emit(PlayerPlayP2pClientEventType.InteractiveQuestionResolved, {
      interactiveQuestionId,
      isLastScored,
    });
  }

  private async handleBenchmark(payload: HostBenchmarkMessage['payload']) {
    const { numMessages, message, sleepBetweenMessages, id } = payload;

    console.debug('Sending benchmark messages', { id, message });

    for (let index = 0; index < numMessages; index += 1) {
      // Emulate time between clicks
      const sleepTime = sleepBetweenMessages
        ? addJitterToTime(5000, { min: 0.1, max: 2 })
        : 0;

      sleep(sleepTime).then(() => {
        try {
          this.sendToHost({
            type: PlayP2pPlayerMessageType.Benchmark,
            payload: { id, slept: sleepTime, message },
          });
        } catch (e) {
          if (e instanceof UnconnectedPeerError) {
            // Stale checker will close it
            return;
          }

          throw e;
        }
      });
    }
  }

  @action
  protected onSetPeers(peers: Record<string, PeerMetadata>) {
    this._players = [this.myPlayerData];

    for (const peerId of Object.keys(peers)) {
      const metadata = peers[peerId];

      this.onPeerConnected(peerId, metadata);
    }

    this.emitter.emit(PlayerPlayP2pClientEventType.PlayersSync, this._players);
  }

  private kickMySelf() {
    this.logger.debug('Host kicked me');

    this.emitter.emit(PlayerPlayP2pClientEventType.Kicked);
    this.dispose();
  }

  private finishGame() {
    this.logger.debug('Host finished the game');

    this.emitter.emit(PlayerPlayP2pClientEventType.GameFinished);

    this.dispose();
  }

  private addNewPlayer(PlayerData: PlayerData) {
    if (this._players.some(player => player.id === PlayerData.id)) {
      return;
    }

    if (PlayerData.id === this.peerId) {
      return;
    }

    this._players = this._players.filter(
      player => player.username !== PlayerData.username,
    );

    this._players.push(PlayerData);
    this.emitter.emit(PlayerPlayP2pClientEventType.PlayerJoined, {
      joinedPlayer: PlayerData,
      allPlayers: [...this._players],
    });
  }

  private removePlayer(playerId: string) {
    this._players = this._players.filter(player => player.id !== playerId);

    this.emitter.emit(PlayerPlayP2pClientEventType.PlayerDisconnected, {
      disconnectedPlayerId: playerId,
      allPlayers: [...this._players],
    });
  }

  private handleGameStateReceivedFromHost(game: GameState | GameStateCompressed) {
    let decompressedGame: GameState;

    if (!isGameState(game)) {
      decompressedGame = this.gameCompressor.decompress(game);
    } else {
      decompressedGame = game;
    }

    if (this.game.version > decompressedGame.version) {
      this.logger.warn('Received game state is outdated. Ignoring it', {
        receivedVersion: decompressedGame.version,
        currentVersion: this.game.version,
      });

      return;
    }

    this.setGame(decompressedGame);

    this.emitter.emit(PlayerPlayP2pClientEventType.GameChanged, decompressedGame);

    this.logger.debug('Game state received from host');
  }

  private handleGameStatePatchesReceivedFromHost(
    gamePatches: GameStatePatchWithVersion[],
  ) {
    if (this._game.version === 0) {
      const firstPatch = gamePatches[0];

      if (firstPatch.version !== 1) {
        // Some patch message was sent in the middle of the join process
        this.finishGameOrRequestGameStateFromHost(gamePatches);
        return;
      }
    }

    for (const patch of gamePatches) {
      try {
        this.applyGamePatches(patch);
      } catch {
        this.finishGameOrRequestGameStateFromHost(gamePatches);
        break;
      }
    }
  }

  private async finishGameOrRequestGameStateFromHost(
    patchesWithVersion: GameStatePatchWithVersion[],
  ) {
    if (hasGameFinishedPatch(patchesWithVersion)) {
      this.logger.debug('Marking game as finished on player');
      this._game.setIsFinished(true);
    } else {
      this.requestGameStateFromHost();
    }
  }

  private requestGameStateFromHost() {
    this.sendToHost({
      type: PlayP2pPlayerMessageType.RequestGameState,
      payload: undefined,
    });
  }

  private handleAllowPlayerToJoin() {
    this.emitter.emit(PlayerPlayP2pClientEventType.AllowPlayerToJoin);

    this.noGameFoundRetries = 0;
  }

  private sendAnyInteractiveQuestionAnswer(
    payload:
      | PlayerSendInteractiveQuestionAnswerMessage['payload']
      | PlayerSendScoreableInteractiveQuestionAnswerMessage['payload'],
    emit: boolean = true,
  ) {
    const isScoreable = (
      p: any,
    ): p is PlayerSendScoreableInteractiveQuestionAnswerMessage['payload'] =>
      'answer' in p;

    const name = isScoreable(payload) ? 'scoreable interactive' : 'interactive';

    return withRetries(
      async () => {
        if (!this.isConnected) {
          throw new Error(
            `Trying to send ${name} answer but not connected to host. Retrying...`,
          );
        }

        if (isScoreable(payload)) {
          await this.sendToHostSync({
            type: PlayP2pPlayerMessageType.SendScoreableInteractiveQuestionAnswer,
            payload,
          });
        } else {
          await this.sendToHostSync({
            type: PlayP2pPlayerMessageType.SendInteractiveQuestionAnswer,
            payload,
          });
        }

        this.logger.debug(`Sent ${name} answer`, { payload });

        if (emit) {
          if (isScoreable(payload)) {
            const isEmptyAnswer =
              'answerIds' in payload.answer && payload.answer.answerIds.length === 0;

            this.emitter.emit(PlayerPlayP2pClientEventType.InteractiveQuestionAnswered, {
              interactiveQuestionId: payload.answer.interactiveQuestionId,
              type: payload.answer.type,
              isEmptyAnswer,
            });
          } else {
            this.emitter.emit(PlayerPlayP2pClientEventType.InteractiveQuestionAnswered, {
              interactiveQuestionId: payload.interactiveQuestionId,
              type: payload.type,
            });
          }
        }
      },
      addJitter(
        createExponentialRetryStrategy({
          step: 1000,
          max: 10000,
          multiplier: 1.5,
          maxRetries: 10,
          shouldRetryError(error) {
            if (error instanceof PeerSyncMessageTimeoutError) {
              // Stale checker will close it
              return false;
            }

            return true;
          },
        }),
        {
          min: 0.5,
          max: 1.5,
        },
      ),
    );
  }

  async sendInteractiveQuestionAnswer(
    payload: PlayerSendInteractiveQuestionAnswerMessage['payload'],
  ) {
    return this.sendAnyInteractiveQuestionAnswer(payload);
  }

  async sendScoreableInteractiveQuestionAnswer(
    payload: PlayerSendScoreableInteractiveQuestionAnswerMessage['payload'],
    emit: boolean = true,
  ) {
    return this.sendAnyInteractiveQuestionAnswer(payload, emit);
  }

  getMyAnswers(): Array<
    PlayerUserAnswer['answer'] & { isResolved: boolean; isCorrect?: boolean }
  > {
    return [...this._game.interactiveQuestionAnswers.values()]
      .flatMap(question =>
        question.playerUserAnswers.map(a => ({
          ...a.asJS,
          isResolved: question.isResolved,
        })),
      )
      .filter(playerAnswer => playerAnswer.player.username === this.username)
      .flatMap(playerAnswer => {
        const { isResolved } = playerAnswer;

        if (isPlayerUserAnswerScoreable(playerAnswer)) {
          return {
            ...playerAnswer.answer,
            isResolved,
            isCorrect: playerAnswer.isCorrect,
          };
        }

        return {
          ...playerAnswer.answer,
          isResolved,
        };
      });
  }

  isResolved(interactiveQuestionId: string) {
    return (
      this._game.getInteractiveQuestionAnswer(interactiveQuestionId)?.isResolved ?? false
    );
  }

  private handleSessionBlocked() {
    this.logger.debug('Session blocked');

    this.emitter.emit(PlayerPlayP2pClientEventType.SessionBlocked);
    this.dispose();
  }

  private handlePlayersLimitReached() {
    this.logger.debug('Players limit reached');

    this.emitter.emit(PlayerPlayP2pClientEventType.PlayersLimitReached);
    this.dispose();
  }
}
